import React from 'react';
import PropTypes from 'prop-types';

import DataSourceValidationConfigsViewRow from 'components/data_sources/single_data_source_view/DataSourceValidationConfigsViewRow';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Table, OverlayTrigger, Tooltip } from 'react-bootstrap';

function DataSourceValidationConfigsView({ attributeMap, validationConfig, setValidationConfig }) {
  const handleSaveExisting = (index, updatedConfig) => {
    const newValidationConfig = [...validationConfig];
    newValidationConfig[index] = updatedConfig;
    setValidationConfig(newValidationConfig);
  };

  const handleSaveNew = newConfig => {
    setValidationConfig([...validationConfig, newConfig]);
  };

  const handleDelete = index => {
    const newValidationConfig = validationConfig.filter((_, i) => i !== index);
    setValidationConfig(newValidationConfig);
  };

  /* eslint-disable react/no-unescaped-entities */
  const renderTooltip = props => (
    <Tooltip id="validation-config-tooltip" {...props}>
      <div style={{ textAlign: 'left' }}>
        <strong>Validation configurations filter rows during data import.</strong>
        <br />
        <br />
        <strong>Examples:</strong>
        <ul style={{ paddingLeft: '20px', margin: '5px 0' }}>
          <li>
            <strong>Greater Than (Numeric):</strong> Column "Pack" &gt; 100
            <br />→ Only imports rows where Pack value is greater than 100
          </li>
          <li>
            <strong>Date Within Last N Days:</strong> Column "Modified Date", value: 3
            <br />→ Only imports rows modified within the last 3 days
          </li>
          <li>
            <strong>Equals:</strong> Column "Status" = "Active"
            <br />→ Only imports rows where Status is "Active"
          </li>
          <li>
            <strong>Contains:</strong> Column "Description" contains "organic"
            <br />→ Only imports rows with "organic" in description
          </li>
        </ul>
        <em>All validations must pass for a row to be imported.</em>
      </div>
    </Tooltip>
  );
  /* eslint-enable react/no-unescaped-entities */

  const renderRecordFormatTooltip = props => (
    <Tooltip id="record-format-tooltip" {...props}>
      <div style={{ textAlign: 'left' }}>
        <strong>Specify the format of values in your source file.</strong>
        <br />
        <br />
        <strong>Date formats:</strong>
        <ul style={{ paddingLeft: '20px', margin: '5px 0' }}>
          <li>YYYY-MM-DD → 2025-12-01</li>
          <li>MM/DD/YYYY → 12/01/2025</li>
          <li>DD/MM/YYYY → 01/12/2025</li>
          <li>YYYY-MM-DD HH:MM:SS → 2025-12-01 14:30:00</li>
        </ul>
        <em>This helps the validator parse values correctly.</em>
      </div>
    </Tooltip>
  );

  return (
    <div>
      <hr />
      <div className="d-flex justify-content-start align-items-center mb-3 font-weight-bold">
        Validation Configurations
        <OverlayTrigger placement="right" delay={{ show: 250, hide: 400 }} overlay={renderTooltip}>
          <span className="ml-2" style={{ cursor: 'help' }}>
            <FontAwesomeIcon icon={faInfoCircle} color="#007bff" />
          </span>
        </OverlayTrigger>
      </div>
      <Table>
        <thead>
          <tr>
            <th>Column Name</th>
            <th>Validation Type</th>
            <th>Validation Value</th>
            <th>
              Record Format
              <OverlayTrigger
                placement="top"
                delay={{ show: 250, hide: 400 }}
                overlay={renderRecordFormatTooltip}
              >
                <span className="ml-2" style={{ cursor: 'help' }}>
                  <FontAwesomeIcon icon={faInfoCircle} size="sm" color="#6c757d" />
                </span>
              </OverlayTrigger>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {validationConfig.map((config, index) => (
            <DataSourceValidationConfigsViewRow
              key={index}
              attributeMap={attributeMap}
              config={config}
              isNewRow={false}
              onSave={updatedConfig => handleSaveExisting(index, updatedConfig)}
              onDelete={() => handleDelete(index)}
            />
          ))}
          <DataSourceValidationConfigsViewRow
            attributeMap={attributeMap}
            config={null}
            isNewRow={true}
            onSave={handleSaveNew}
          />
        </tbody>
      </Table>
    </div>
  );
}

DataSourceValidationConfigsView.propTypes = {
  attributeMap: PropTypes.object.isRequired,
  validationConfig: PropTypes.array.isRequired,
  setValidationConfig: PropTypes.func.isRequired,
};

export default DataSourceValidationConfigsView;
